import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  RiArrowRightLine, RiShieldStarLine, RiLockLine, RiDatabaseLine,
  RiMailLine, RiFileInfoLine, RiUserStarLine, RiShieldCheckLine,
  RiTimeLine, RiDeleteBinLine, RiEyeLine, RiEditLine,
  RiProhibitedLine, RiAddCircleLine, RiSettings3Line,
  RiCheckDoubleLine, RiGlobalLine, RiServerLine, RiAlertLine,
  RiPhoneLine, RiArrowUpLine,
} from 'react-icons/ri';

/* ─── THEME COLORS (Teal & Gold Palette matching About page) ───────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: ${T.borderLight}; }
  ::-webkit-scrollbar-thumb { background: ${T.primary}; border-radius: 4px; }
  
  @media (max-width: 768px) {
    section { padding: 48px 24px !important; }
  }
`;

const stats = [
  { num: '256-bit', label: 'AES Encryption', icon: RiLockLine, grad: T.gradPrimary },
  { num: '30 days', label: 'Response Guarantee', icon: RiTimeLine, grad: T.gradAccent },
  { num: 'Zero', label: 'Third-party Data Sales', icon: RiShieldCheckLine, grad: T.gradSuccess },
  { num: 'SOC 2', label: 'Type II Certified', icon: RiServerLine, grad: T.gradGold },
];

const sections = [
  {
    icon: RiDatabaseLine,
    title: 'Information We Collect',
    grad: T.gradPrimary,
    bullets: ['Name, email, phone & company details', 'Payment information during registration', 'Device & usage patterns for improvements'],
    content: 'We collect information you provide directly when registering or interacting with our services. We also automatically gather technical data about your device and usage patterns to improve your experience and ensure platform reliability.',
  },
  {
    icon: RiShieldStarLine,
    title: 'How We Use Your Data',
    grad: T.gradAccent,
    bullets: ['Registration & event communications', 'Personalised summit experiences', 'Relevant future event updates'],
    content: 'Your information powers registration processing, event communications, service improvements, and personalised experiences. We may also send updates about future summits and opportunities aligned with your professional interests.',
  },
  {
    icon: RiLockLine,
    title: 'Data Security',
    grad: T.gradSuccess,
    bullets: ['256-bit AES encryption at rest & in transit', 'Regular third-party security audits', 'Continuous threat monitoring'],
    content: 'We implement industry-standard security measures including 256-bit encryption, secure server infrastructure, and regular independent security audits. Our team continuously monitors for threats and vulnerabilities.',
  },
  {
    icon: RiUserStarLine,
    title: 'Information Sharing',
    grad: T.gradGold,
    bullets: ['We never sell your personal data', 'Trusted processors under strict NDAs', 'Payment partners under PCI-DSS'],
    content: 'We do not sell your personal information. Trusted service providers — payment processors, email platforms — may access your data under strict confidentiality agreements solely to help us operate the summit.',
  },
  {
    icon: RiFileInfoLine,
    title: 'Cookies & Tracking',
    grad: 'linear-gradient(135deg, #D4A017 0%, #6BC2B5 100%)',
    bullets: ['Session & preference cookies', 'Analytics to improve site performance', 'Full control via browser settings'],
    content: 'We use cookies and similar technologies to enhance your experience, analyse traffic, and personalise content. You can control preferences through your browser settings, though disabling cookies may affect certain features.',
  },
  {
    icon: RiTimeLine,
    title: 'Retention Policy',
    grad: 'linear-gradient(135deg, #1F7A6E 0%, #3BBCAE 100%)',
    bullets: ['Event data retained for 3 years', 'Deletion requests honoured in 30 days', 'Legal holds communicated transparently'],
    content: 'We retain your data only as long as necessary to provide services and meet legal obligations. Event registration data is typically retained for 3 years. You may request deletion at any time via our privacy team.',
  },
];

const lifecycleSteps = [
  { icon: RiAddCircleLine, title: 'Collection', color: T.primary, desc: 'You submit your details during registration or account creation. We collect only what is necessary to deliver a world-class summit experience — nothing superfluous.' },
  { icon: RiShieldCheckLine, title: 'Secure Storage', color: T.accent, desc: 'Your data is encrypted at rest and in transit using AES-256. Servers are hosted in SOC 2 Type II certified data centres with multi-layer access controls and zero-trust architecture.' },
  { icon: RiSettings3Line, title: 'Processing', color: T.success, desc: 'Data is processed solely to deliver the services you signed up for — registration confirmation, event updates, networking features, and post-event resources.' },
  { icon: RiDeleteBinLine, title: 'Deletion on Request', color: T.accentGold, desc: 'Upon request we permanently erase your personal data from all systems within 30 days, except where retention is required by applicable law. We confirm deletion in writing.' },
];

const rights = [
  { icon: RiEyeLine, label: 'Access your data', desc: 'Request a full copy of all personal data we hold about you.' },
  { icon: RiEditLine, label: 'Correct errors', desc: 'Update or correct inaccurate personal information at any time.' },
  { icon: RiDeleteBinLine, label: 'Request deletion', desc: 'Ask us to permanently erase your personal data from our systems.' },
  { icon: RiProhibitedLine, label: 'Object to processing', desc: 'Restrict how we process your information in certain circumstances.' },
];

/* ─── SECTION LABEL ──────────────────────────────────────────────────── */
function SectionLabel({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: T.softTeal, border: `1px solid ${T.border}`, borderRadius: 40, padding: '5px 16px', whiteSpace: 'nowrap' }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.primary }} />
        <span style={{ fontSize: 10, fontWeight: 800, color: T.primary, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${T.border}, transparent)` }} />
    </div>
  );
}

/* ─── POLICY CARD ────────────────────────────────────────────────────── */
function PolicyCard({ section, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [hov, setHov] = React.useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.07 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: T.white,
        border: `1px solid ${hov ? T.primary : T.border}`,
        borderRadius: 20,
        padding: '28px 28px 26px',
        transition: 'all 0.3s ease',
        transform: hov ? 'translateY(-5px)' : 'translateY(0)',
        boxShadow: hov ? T.shadowLg : T.shadow,
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: section.grad, opacity: hov ? 1 : 0.5, transition: 'opacity 0.3s' }} />
      <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, background: section.grad, opacity: 0.06, filter: 'blur(20px)', borderRadius: '50%' }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 18 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: section.grad, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: T.shadow }}>
          <section.icon size={22} color={T.white} />
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 800, color: T.text, lineHeight: 1.3, paddingTop: 6 }}>{section.title}</h3>
      </div>

      <p style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.75, marginBottom: 18 }}>{section.content}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {section.bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: T.softTeal, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <RiCheckDoubleLine size={10} color={T.primary} />
            </div>
            <span style={{ fontSize: 12.5, color: T.textMuted }}>{b}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── MAIN ───────────────────────────────────────────────────────────── */
export default function PrivacyPolicy() {
  const [showBtt, setShowBtt] = useState(false);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif", color: T.text }}>

        {/* ── HERO SECTION (Matching Topics/About) ──────────────────────────────────── */}
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ 
                padding: '5px 14px', 
                borderRadius: 20, 
                background: T.softTeal, 
                border: `1px solid ${T.border}`, 
                color: T.primary, 
                fontSize: 11, 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                letterSpacing: '0.1em' 
              }}>
                <RiShieldStarLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Privacy & Security
              </span>
            </div>
            <h1 style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', 
              fontWeight: 900, 
              color: T.navy, 
              letterSpacing: '-0.03em', 
              lineHeight: 1.06, 
              marginBottom: 14 
            }}>
              Your Privacy <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Matters</em> to Us
            </h1>
            <p style={{ 
              fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', 
              color: T.textMid, 
              lineHeight: 1.7, 
              maxWidth: 560 
            }}>
              A complete, transparent overview of how Meetings21 collects, uses, and protects your information — written in plain language.
            </p>
            
            {/* Decorative line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>256-bit</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>AES Encryption</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>30 days</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Response Guarantee</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>Zero</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Third-party Data Sales</div>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ──────────────────────────────────────────────────── */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 80px' }}>

          {/* Intro banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 20,
              padding: '32px 40px',
              marginBottom: 60,
              display: 'flex',
              gap: 32,
              alignItems: 'flex-start',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: T.shadowLg,
            }}
          >
            <div style={{ position: 'absolute', right: -20, top: -20, width: 180, height: 180, background: T.gradPrimary, opacity: 0.05, filter: 'blur(30px)', borderRadius: '50%' }} />
            <div style={{ width: 52, height: 52, borderRadius: 16, background: T.gradPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: T.shadow }}>
              <RiShieldStarLine size={26} color={T.white} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.primary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Last Updated — January 15, 2025</div>
              <p style={{ fontSize: 15, color: T.textMid, lineHeight: 1.8, maxWidth: 860 }}>
                At <span style={{ color: T.primary, fontWeight: 700 }}>Meetings21</span>, we take your privacy seriously. This policy explains how we collect, use, disclose, and safeguard your information when you visit our website, register for events, or interact with our services.
              </p>
            </div>
          </motion.div>

          {/* Policy Sections */}
          <SectionLabel label="Policy Sections" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 64 }}>
            {sections.map((section, i) => <PolicyCard key={i} section={section} index={i} />)}
          </div>

          {/* Data Lifecycle */}
          <SectionLabel label="Data Lifecycle" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 24,
              padding: '44px 48px',
              marginBottom: 64,
              boxShadow: T.shadowLg,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 36 }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: T.gradPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: T.shadow }}>
                <RiGlobalLine size={22} color={T.white} />
              </div>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>How your data flows at Meetings21</h2>
                <p style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>A transparent end-to-end view of your data journey</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, position: 'relative' }}>
              <div style={{ position: 'absolute', top: 24, left: '12.5%', right: '12.5%', height: 2, background: `linear-gradient(90deg, ${T.primary}, ${T.accent}, ${T.success}, ${T.accentGold})`, opacity: 0.3, zIndex: 0 }} />

              {lifecycleSteps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  style={{ textAlign: 'center', padding: '0 20px', position: 'relative', zIndex: 1 }}
                >
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: T.white, border: `2px solid ${step.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: `0 0 20px ${step.color}30` }}>
                    <step.icon size={20} color={step.color} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: step.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Step {i + 1}</div>
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 10 }}>{step.title}</h4>
                  <p style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.7 }}>{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Your Rights */}
          <SectionLabel label="Your Rights" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 64 }}>
            {rights.map((r, i) => {
              const grads = [T.gradPrimary, T.gradAccent, T.gradSuccess, T.gradGold];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ y: -5 }}
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: 20,
                    padding: '28px 22px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: T.shadow,
                  }}
                >
                  <div style={{ position: 'absolute', top: -16, right: -16, width: 80, height: 80, background: grads[i], opacity: 0.08, filter: 'blur(14px)', borderRadius: '50%' }} />
                  <div style={{ width: 46, height: 46, borderRadius: 14, background: grads[i], display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: T.shadow }}>
                    <r.icon size={22} color={T.white} />
                  </div>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>{r.label}</h4>
                  <p style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.7 }}>{r.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Section - Matching Topics style with Gold */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              background: T.gradNavy,
              borderRadius: 28,
              padding: '60px 64px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: T.shadowLg,
            }}
          >
            <div style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 500, height: 300, background: 'rgba(255,255,255,0.1)', opacity: 0.15, filter: 'blur(50px)', borderRadius: '50%', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -60, right: -60, width: 300, height: 300, background: 'rgba(255,255,255,0.08)', opacity: 0.1, filter: 'blur(40px)', borderRadius: '50%', pointerEvents: 'none' }} />

            <div style={{ width: 56, height: 56, borderRadius: 18, background: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: T.shadow }}>
              <RiMailLine size={26} color={T.primary} />
            </div>

            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: T.white, marginBottom: 14, position: 'relative', letterSpacing: '-0.5px' }}>
              Questions About Your Privacy?
            </h3>
            <p style={{ fontSize: 15, color: `${T.white}cc`, marginBottom: 32, lineHeight: 1.75, maxWidth: 520, margin: '0 auto 32px', position: 'relative' }}>
              Our Privacy Team responds to all requests within 30 days. We're here to help you understand exactly how your data is handled.
            </p>

            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
             
            </div>
          </motion.div>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────────────── */}
    

        {/* BACK TO TOP - Gold themed */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
}